import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import type { ItemDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import { conteoDeRegistrosDelItem, etiquetaDelCruceDeAgua } from "./aguasTerreno";
import type { OpcionParaVincular, TerrenoDeLaLista } from "./plantacionesTerreno";
import { NotaDeCreadaEnTerreno } from "./NotaDeCreadaEnTerreno";
import { BloqueDeRegistros, ContadorDeRegistros } from "./RegistrosDeTerrenoDeLaFila";

interface FilaDeTerrenoDeLaPlantacionProps {
  terreno: TerrenoDeLaLista;
  /** Cómo se llama la fila en el encabezado del registro y en los nombres accesibles. */
  titulo: string;
  item: ItemDeTerreno | null;
  /** La fila tiene (o va a tener) un registro de terreno enlazado: lleva la insignia. */
  deTerreno: boolean;
  /** Solo en las filas TERRENO sin guardar: el uuid de la plantación de la App. */
  uuidPorEnlazar?: string;
  /** Con qué filas guardadas puede decir "es la misma que". */
  opciones: OpcionParaVincular[];
  abierto: boolean;
  ocupada: boolean;
  /** Cuántas columnas tiene la tabla: la línea ocupa el ancho entero. */
  columnas: number;
  onAlternar: () => void;
  /** Nació en la App y todavía no tiene superficie ni densidad: lleva la nota azul. */
  creadaSinValorizar?: boolean;
}

/**
 * La línea de debajo de cada fila de la tabla de plantaciones: de dónde viene
 * (insignia "Terreno"), cuánto trajo la visita (contador que abre el registro)
 * y, si la plantación nació en la App y todavía no se guardó, qué falta y con
 * qué fila ya cargada se puede confundir.
 *
 * Es de LECTURA: el registro de terreno propone y nunca copia nada a la fila.
 * NO HAY "No está en el predio": el diseño no lo dibuja para plantaciones.
 */
export function FilaDeTerrenoDeLaPlantacion({
  terreno,
  titulo,
  item,
  deTerreno,
  uuidPorEnlazar,
  opciones,
  abierto,
  ocupada,
  columnas,
  onAlternar,
  creadaSinValorizar = false,
}: FilaDeTerrenoDeLaPlantacionProps) {
  const conteo = conteoDeRegistrosDelItem(item);
  const etiqueta = etiquetaDelCruceDeAgua(terreno.modo, conteo);
  const pideDatos = uuidPorEnlazar !== undefined;

  // Una fila hecha a mano, sin terreno que cruzar (módulo apagado), no dibuja
  // una línea vacía.
  if (!deTerreno && terreno.modo === "oculto") return null;

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columnas} className="p-0">
        <div className="flex flex-wrap items-center gap-2.5 py-0 pr-5 pb-2 pl-10">
          {deTerreno ? (
            <span
              title="Nació en la App de terreno"
              className="bg-info-soft text-info-soft-foreground border-info-dot/30 inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[0.625rem] font-extrabold tracking-[0.05em] uppercase"
            >
              <span aria-hidden="true" className="bg-info-dot size-1.5 rounded-full" />
              Terreno
            </span>
          ) : null}

          {conteo.total > 0 ? (
            <ContadorDeRegistros
              conteo={conteo}
              abierto={abierto}
              etiqueta={`Registro de terreno de ${titulo}`}
              onAlternar={onAlternar}
            />
          ) : etiqueta ? (
            <span
              className={cn(
                "rounded-sm px-[0.4375rem] py-0.5 text-[0.6875rem] font-bold",
                etiqueta === "Visita pendiente"
                  ? "bg-warning-soft text-warning-soft-foreground"
                  : "bg-muted text-ink-muted",
              )}
            >
              {etiqueta}
            </span>
          ) : null}

          {pideDatos && opciones.length > 0 ? (
            <label className="text-ink-body ml-auto inline-flex items-center gap-2 text-[0.71875rem] font-bold">
              Es la misma que
              <Select
                value=""
                disabled={ocupada}
                onValueChange={(clave) => terreno.onVincular(uuidPorEnlazar, Number(clave))}
              >
                <SelectTrigger
                  size="sm"
                  className="max-w-72 rounded-[0.5625rem] px-2 text-xs font-semibold"
                  aria-label={`Es la misma que, ${titulo}`}
                >
                  <SelectValue placeholder="Elegir plantación…" />
                </SelectTrigger>
                <SelectContent>
                  {opciones.map((opcion) => (
                    <SelectItem key={opcion.clave} value={opcion.clave}>
                      {opcion.etiqueta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          ) : null}
        </div>

        {creadaSinValorizar && (abierto || conteo.total === 0 || pideDatos) ? (
          <NotaDeCreadaEnTerreno className="bg-info-soft text-info-soft-foreground border-info-dot/30 mr-5 mb-2 ml-10 rounded-lg border px-3.5 py-2.5 text-xs font-semibold text-pretty" />
        ) : null}

        {abierto && item ? (
          <BloqueDeRegistros
            informeId={terreno.informeId}
            item={item}
            titulo={titulo}
            encargado={terreno.encargado}
            onAbrirFoto={terreno.onAbrirFoto}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
}
